import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch as MUISwitch,
  TextField as MUITextField,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Modal from "components/basic/modal";
import Button from "components/basic/button";
import Typography from "components/basic/typography";

import { DateField, TimeField } from "./pickers";
import {
  CustomEntry,
  CustomRange,
  CustomSingle,
  PerDayHours,
  RangeAvailability,
  SingleAvailability,
} from "./types";
import { DATE_FORMAT } from "./constants";
import { enumerateDates, formatDateShort, genId } from "./helpers";

interface AddCustomDateModalProps {
  existing?: CustomEntry | null;
  onClose: () => void;
  onSave: (entry: CustomEntry) => void;
}

const DEFAULT_FROM = "09:00";
const DEFAULT_TO = "17:00";

const buildPerDay = (
  start: string,
  end: string,
  prev: PerDayHours[],
): PerDayHours[] => {
  const prevByDate = new Map(prev.map((p) => [p.date, p]));
  return enumerateDates(start, end).map(
    (date) =>
      prevByDate.get(date) ?? {
        date,
        available: true,
        from: DEFAULT_FROM,
        to: DEFAULT_TO,
      },
  );
};

const InfoBanner: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        backgroundColor: "rgba(33, 150, 243, 0.08)",
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
      <Typography
        variant="caption"
        style={{ color: theme.palette.info.main, lineHeight: 1.4 }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const AddCustomDateModal: React.FC<AddCustomDateModalProps> = ({
  existing,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const today = moment().format(DATE_FORMAT);

  const [mode, setMode] = useState<"single" | "range">(
    existing?.mode ?? "single",
  );

  // single
  const [singleDate, setSingleDate] = useState(
    existing?.mode === "single" ? existing.date : today,
  );
  const [singleAvail, setSingleAvail] = useState<SingleAvailability>(
    existing?.mode === "single" ? existing.availability : "unavailable",
  );
  const [singleFrom, setSingleFrom] = useState(
    (existing?.mode === "single" && existing.from) || DEFAULT_FROM,
  );
  const [singleTo, setSingleTo] = useState(
    (existing?.mode === "single" && existing.to) || "13:00",
  );

  // range
  const [startDate, setStartDate] = useState(
    existing?.mode === "range"
      ? existing.startDate
      : moment().format(DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(
    existing?.mode === "range"
      ? existing.endDate
      : moment().add(1, "day").format(DATE_FORMAT),
  );
  const [rangeAvail, setRangeAvail] = useState<RangeAvailability>(
    existing?.mode === "range" ? existing.availability : "unavailable",
  );
  const [rangeFrom, setRangeFrom] = useState(
    (existing?.mode === "range" && existing.from) || DEFAULT_FROM,
  );
  const [rangeTo, setRangeTo] = useState(
    (existing?.mode === "range" && existing.to) || DEFAULT_TO,
  );
  const [perDay, setPerDay] = useState<PerDayHours[]>(
    (existing?.mode === "range" && existing.perDay) || [],
  );

  const [note, setNote] = useState(existing?.note ?? "");

  // Keep the per-day list in sync with the selected range.
  useEffect(() => {
    if (mode !== "range" || rangeAvail !== "perDay") {
      return;
    }
    if (moment(endDate).isBefore(moment(startDate), "day")) {
      return;
    }
    setPerDay((prev) => buildPerDay(startDate, endDate, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rangeAvail, startDate, endDate]);

  const isEdit = Boolean(existing);

  const rangeInvalid =
    mode === "range" && moment(endDate).isBefore(moment(startDate), "day");

  const infoText = useMemo(() => {
    if (mode === "single") {
      return singleAvail === "unavailable"
        ? t("custom_date.info_single_unavailable")
        : t("custom_date.info_single_hours");
    }
    if (rangeAvail === "sameHours") {
      return t("custom_date.info_range_hours", {
        start: formatDateShort(startDate),
        end: formatDateShort(endDate),
      });
    }
    if (rangeAvail === "unavailable") {
      return t("custom_date.info_range_unavailable");
    }
    return "";
  }, [mode, singleAvail, rangeAvail, startDate, endDate, t]);

  const updatePerDay = (date: string, patch: Partial<PerDayHours>) =>
    setPerDay((prev) =>
      prev.map((d) => (d.date === date ? { ...d, ...patch } : d)),
    );

  const handleSave = () => {
    if (rangeInvalid) {
      return;
    }
    const trimmedNote = note.trim() || undefined;

    if (mode === "single") {
      const entry: CustomSingle = {
        id: existing?.id ?? genId(),
        mode: "single",
        date: singleDate,
        availability: singleAvail,
        ...(singleAvail === "custom" ? { from: singleFrom, to: singleTo } : {}),
        note: trimmedNote,
      };
      onSave(entry);
      return;
    }

    const entry: CustomRange = {
      id: existing?.id ?? genId(),
      mode: "range",
      startDate,
      endDate,
      availability: rangeAvail,
      ...(rangeAvail === "sameHours" ? { from: rangeFrom, to: rangeTo } : {}),
      ...(rangeAvail === "perDay"
        ? { perDay: buildPerDay(startDate, endDate, perDay) }
        : {}),
      note: trimmedNote,
    };
    onSave(entry);
  };

  const sectionLabel = (text: string) => (
    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
      {text}
    </Typography>
  );

  return (
    <Modal onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            width: { xs: "86vw", sm: 460 },
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              mb: 1,
            }}
          >
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {isEdit
                ? t("custom_date.edit_title")
                : t("custom_date.add_title")}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: "absolute", right: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary, marginBottom: 12 }}
          >
            {isEdit ? t("custom_date.edit_intro") : t("custom_date.add_intro")}
          </Typography>

          {/* Single vs Range */}
          {!isEdit && (
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value as "single" | "range")}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value="single"
                control={<Radio color="warning" size="small" />}
                label={t("custom_date.single")}
              />
              <FormControlLabel
                value="range"
                control={<Radio color="warning" size="small" />}
                label={t("custom_date.range")}
              />
            </RadioGroup>
          )}

          {/* Date inputs */}
          {mode === "single" ? (
            <Box sx={{ mb: 2 }}>
              {sectionLabel(t("custom_date.date"))}
              <Box sx={{ mt: 0.5 }}>
                <DateField value={singleDate} onChange={setSingleDate} />
              </Box>
            </Box>
          ) : (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                {sectionLabel(t("custom_date.from"))}
                <Box sx={{ mt: 0.5 }}>
                  <DateField value={startDate} onChange={setStartDate} />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                {sectionLabel(t("custom_date.to"))}
                <Box sx={{ mt: 0.5 }}>
                  <DateField
                    value={endDate}
                    onChange={setEndDate}
                    minDate={startDate}
                  />
                </Box>
              </Box>
            </Stack>
          )}

          {rangeInvalid && (
            <Typography
              variant="caption"
              color="error"
              style={{ display: "block", marginBottom: 8 }}
            >
              {t("custom_date.end_before_start")}
            </Typography>
          )}

          {/* Availability options */}
          {sectionLabel(
            mode === "single"
              ? t("custom_date.availability_single")
              : t("custom_date.availability_range"),
          )}

          {mode === "single" ? (
            <RadioGroup
              value={singleAvail}
              onChange={(e) =>
                setSingleAvail(e.target.value as SingleAvailability)
              }
              sx={{ mt: 0.5 }}
            >
              <FormControlLabel
                value="unavailable"
                control={<Radio color="warning" size="small" />}
                label={t("custom_date.fully_unavailable")}
              />
              <FormControlLabel
                value="custom"
                control={<Radio color="warning" size="small" />}
                label={t("custom_date.set_custom")}
              />
            </RadioGroup>
          ) : (
            <RadioGroup
              value={rangeAvail}
              onChange={(e) =>
                setRangeAvail(e.target.value as RangeAvailability)
              }
              sx={{ mt: 0.5 }}
            >
              <FormControlLabel
                value="unavailable"
                control={<Radio color="warning" size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {t("custom_date.fully_unavailable")}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("custom_date.unavailable_hint")}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="sameHours"
                control={<Radio color="warning" size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {t("custom_date.same_hours")}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("custom_date.same_hours_hint")}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="perDay"
                control={<Radio color="warning" size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {t("custom_date.per_day")}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("custom_date.per_day_hint")}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          )}

          {/* Hour inputs */}
          {mode === "single" && singleAvail === "custom" && (
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
              <Box>
                {sectionLabel(t("custom_date.from"))}
                <Box sx={{ mt: 0.5 }}>
                  <TimeField value={singleFrom} onChange={setSingleFrom} />
                </Box>
              </Box>
              <Box>
                {sectionLabel(t("custom_date.to"))}
                <Box sx={{ mt: 0.5 }}>
                  <TimeField value={singleTo} onChange={setSingleTo} />
                </Box>
              </Box>
            </Stack>
          )}

          {mode === "range" && rangeAvail === "sameHours" && (
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
              <Box>
                {sectionLabel(t("custom_date.from"))}
                <Box sx={{ mt: 0.5 }}>
                  <TimeField value={rangeFrom} onChange={setRangeFrom} />
                </Box>
              </Box>
              <Box>
                {sectionLabel(t("custom_date.to"))}
                <Box sx={{ mt: 0.5 }}>
                  <TimeField value={rangeTo} onChange={setRangeTo} />
                </Box>
              </Box>
            </Stack>
          )}

          {mode === "range" && rangeAvail === "perDay" && !rangeInvalid && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {perDay.map((day) => (
                <Stack
                  key={day.date}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Box sx={{ width: 92, flexShrink: 0 }}>
                    <Typography variant="body2">
                      {moment(day.date).format("ddd, MMM D")}
                    </Typography>
                  </Box>
                  <MUISwitch
                    size="small"
                    color="warning"
                    checked={day.available}
                    onChange={(e) =>
                      updatePerDay(day.date, { available: e.target.checked })
                    }
                  />
                  {day.available ? (
                    <>
                      <TimeField
                        value={day.from}
                        minWidth={110}
                        onChange={(t) => updatePerDay(day.date, { from: t })}
                      />
                      <Typography variant="body2">-</Typography>
                      <TimeField
                        value={day.to}
                        minWidth={110}
                        onChange={(t) => updatePerDay(day.date, { to: t })}
                      />
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("custom_date.unavailable")}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          )}

          {/* Note */}
          <Box sx={{ mt: 2 }}>
            {sectionLabel(t("custom_date.note"))}
            <MUITextField
              fullWidth
              size="small"
              placeholder={t("custom_date.note_placeholder")}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 100))}
              inputProps={{ maxLength: 100 }}
              helperText={`${note.length}/100`}
              FormHelperTextProps={{ sx: { textAlign: "right", m: 0 } }}
              sx={{ mt: 0.5 }}
            />
          </Box>

          {infoText && (
            <Box sx={{ mt: 1.5 }}>
              <InfoBanner text={infoText} />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Footer */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" color="primary" onClick={onClose}>
              {t("custom_date.cancel")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={rangeInvalid}
              onClick={handleSave}
            >
              {isEdit ? t("custom_date.save_changes") : t("custom_date.add")}
            </Button>
          </Stack>
        </Box>
      </LocalizationProvider>
    </Modal>
  );
};

export default AddCustomDateModal;
